import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, Send, Mail, DollarSign, FileText, CalendarClock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  generateFacilityEmailTemplate, sendFacilityEmail,
  generateFacilityInvoice, generateFacilityContract,
  EMAIL_TEMPLATE_KINDS, canonicalFacilityStatus,
} from "@/lib/api";

const stageDefaultKind = (stage) => {
  const c = canonicalFacilityStatus(stage);
  const match = EMAIL_TEMPLATE_KINDS.find((k) => k.stage === c);
  return match?.value || "follow_up";
};

// =============================================================================
// AI Email Composer — stage-aware template dropdown + Generate button + Send
// =============================================================================
export function AIEmailComposerDialog({ facility, open, onClose, onSent, initialKind }) {
  const [kind, setKind] = useState(initialKind || stageDefaultKind(facility?.status));
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [customContext, setCustomContext] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setKind(initialKind || stageDefaultKind(facility?.status));
      setSubject(""); setBody(""); setCustomContext(""); setError("");
    }
    // eslint-disable-next-line
  }, [open, facility?.id]);

  const generate = async () => {
    try {
      setGenerating(true); setError("");
      const res = await generateFacilityEmailTemplate(facility.id, {
        template_kind: kind,
        custom_context: customContext || undefined,
      });
      setSubject(res.subject || "");
      setBody(res.body || "");
      toast.success("AI drafted the email — review and edit as needed.");
    } catch (e) {
      setError(e?.response?.data?.detail || "AI generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      setError("Subject and body are required."); return;
    }
    try {
      setSending(true); setError("");
      const kindMap = {
        introduction: "introduction",
        follow_up: "follow_up",
        needs_assessment: "needs_assessment",
        quote_delivery: "quote_sent",
        negotiation: "revised_quote",
        contract_ready: "contract_ready",
        welcome: "welcome",
        renewal: "renewal_reminder",
      };
      const res = await sendFacilityEmail(facility.id, {
        action: kindMap[kind] || "custom",
        subject: subject.trim(),
        body_text: body.trim(),
        body_html: null,
      });
      const provider = res?.provider || "resend";
      if (provider === "gmail") {
        toast.success(`Email sent from Gmail (${res?.from || "your account"}) — reply auto-attaches here.`);
      } else {
        toast.success("Email sent — delivery tracked in Activity.");
      }
      onSent?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Send failed — check Resend key.";
      setError(detail);
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="ai-email-composer">
        <DialogHeader className="px-6 pt-5 pb-3 border-b" style={{ borderColor: "#F1F5F9" }}>
          <DialogTitle>Compose email · {facility?.facility_name}</DialogTitle>
          <DialogDescription>
            Pick a template (auto-matched to the current pipeline stage), let AI draft it, edit, and send via Resend.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
            <div>
              <Label className="text-xs">Template</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-10" data-testid="email-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EMAIL_TEMPLATE_KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={generate} disabled={generating}
              className="h-10"
              style={{ background: "#D4AF37", color: "#0F172A" }}
              data-testid="ai-email-generate"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              {subject ? "Regenerate" : "Draft with AI"}
            </Button>
          </div>

          <div>
            <Label className="text-xs">Extra context for AI (optional)</Label>
            <Input
              value={customContext}
              onChange={(e) => setCustomContext(e.target.value)}
              placeholder="e.g. Sarah asked for a bundled quarterly option"
              className="mt-1 h-9 text-sm"
              data-testid="ai-email-context"
            />
          </div>

          <div>
            <Label className="text-xs">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1"
              placeholder="Subject line"
              data-testid="ai-email-subject"
            />
          </div>

          <div>
            <Label className="text-xs">Body</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="mt-1 text-sm"
              placeholder="Email body — will be sent as plain text with line breaks."
              data-testid="ai-email-body"
            />
          </div>

          {error ? (
            <div
              className="rounded-lg border p-3 text-xs"
              style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
              data-testid="ai-email-error"
            >{error}</div>
          ) : null}
        </div>

        <DialogFooter className="px-6 py-4 border-t" style={{ borderColor: "#F1F5F9" }}>
          <Button variant="outline" onClick={onClose} className="h-9" data-testid="ai-email-cancel">Cancel</Button>
          <Button
            onClick={send} disabled={sending || !subject || !body}
            className="h-9"
            style={{ background: "#0B2545", color: "#FFF" }}
            data-testid="ai-email-send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Generate Invoice Dialog
// =============================================================================
export function GenerateInvoiceDialog({ facility, open, onClose, onCreated }) {
  const suggested = facility?.monthly_revenue || facility?.contract_value || "";
  const [amount, setAmount] = useState(suggested);
  const [description, setDescription] = useState(
    `Monthly service — ${facility?.facility_name || "Facility"}`
  );
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setAmount(facility?.monthly_revenue || facility?.contract_value || "");
      setDescription(`Monthly service — ${facility?.facility_name || "Facility"}`);
      setDueDate(""); setError("");
    }
    // eslint-disable-next-line
  }, [open, facility?.id]);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setError("Amount must be greater than 0."); return; }
    try {
      setSaving(true); setError("");
      const res = await generateFacilityInvoice(facility.id, {
        amount: amt,
        description: description || undefined,
        due_date: dueDate || undefined,
      });
      const link = res?.invoice?.stripe_payment_link;
      toast.success(link ? "Invoice created + Stripe link sent." : "Invoice created and emailed.");
      onCreated?.(res.invoice);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create invoice.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="generate-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Generate invoice · {facility?.facility_name}</DialogTitle>
          <DialogDescription>
            Creates a facility invoice, generates a Stripe checkout link (if configured), and emails it to the billing contact.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error ? (
            <div className="rounded-lg border p-3 text-xs"
              style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
              data-testid="invoice-error"
            >{error}</div>
          ) : null}
          <div>
            <Label className="text-xs">Amount (USD)</Label>
            <Input
              type="number" min="1" step="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-1" data-testid="invoice-amount"
            />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input
              value={description} onChange={(e) => setDescription(e.target.value)}
              className="mt-1" data-testid="invoice-description"
            />
          </div>
          <div>
            <Label className="text-xs">Due Date (optional)</Label>
            <Input
              type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
              className="mt-1" data-testid="invoice-due-date"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button
            onClick={submit} disabled={saving} className="h-9"
            style={{ background: "#0B2545", color: "#FFF" }}
            data-testid="invoice-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <DollarSign className="h-4 w-4 mr-1.5" />}
            Create Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Generate Contract Dialog
// =============================================================================
export function GenerateContractDialog({ facility, open, onClose, onCreated }) {
  const [title, setTitle] = useState(`${facility?.facility_name || "Facility"} — Service Agreement`);
  const [value, setValue] = useState(facility?.contract_value || facility?.monthly_revenue || "");
  const [startDate, setStartDate] = useState(facility?.contract_start_date || "");
  const [endDate, setEndDate] = useState(facility?.contract_end_date || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setTitle(`${facility?.facility_name || "Facility"} — Service Agreement`);
      setValue(facility?.contract_value || facility?.monthly_revenue || "");
      setStartDate(facility?.contract_start_date || "");
      setEndDate(facility?.contract_end_date || "");
      setNotes(""); setError("");
    }
    // eslint-disable-next-line
  }, [open, facility?.id]);

  const submit = async () => {
    try {
      setSaving(true); setError("");
      const res = await generateFacilityContract(facility.id, {
        title: title || undefined,
        value: value === "" ? undefined : Number(value),
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        notes: notes || undefined,
      });
      toast.success("Contract draft created.");
      onCreated?.(res.contract);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create contract.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="generate-contract-dialog">
        <DialogHeader>
          <DialogTitle>Generate contract · {facility?.facility_name}</DialogTitle>
          <DialogDescription>
            Creates a draft contract linked to this facility. Upload the signed PDF later from the Contracts module.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error ? (
            <div className="rounded-lg border p-3 text-xs"
              style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
              data-testid="contract-error"
            >{error}</div>
          ) : null}
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="contract-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Value (USD)</Label>
              <Input type="number" min="0" step="1" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1" data-testid="contract-value" />
            </div>
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" data-testid="contract-start" />
            </div>
            <div>
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1" data-testid="contract-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="contract-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="h-9"
            style={{ background: "#0B2545", color: "#FFF" }}
            data-testid="contract-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileText className="h-4 w-4 mr-1.5" />}
            Create Contract
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
